import React, { useState, useEffect, useMemo } from 'react'
import { Card, Nav, NavItem, NavLink, TabContent } from 'reactstrap'
import { isEmpty, isObject } from 'lodash'
import classnames from 'classnames'
// @ts-ignore
import { Field, reduxForm } from 'redux-form'
import { compose } from 'redux'
import { useTranslation } from 'react-i18next'
import ReactLoading from 'react-loading'
import { code } from 'currency-codes'

import validate from 'Containers/Validation/validate'
import renderSelectField from 'Shared/components/form/Select'
import TransferwiseSvg from 'Shared/img/transferwise.svg'
import Alert from 'Shared/components/Alert'
import BankDetailsForm from './BankDetailsForm'
import { CountriesProps } from '../SettingsProps'

import {
  TransferwiseRequirement,
  Recipient,
  RecipientAddress,
} from '../../../Types'

interface PayoutProps {
  currencies: [
    {
      code: string
      hasBankDetails: boolean
      payInAllowed: boolean
      sampleBankDetails: null
    }
  ]
  requirements: Array<TransferwiseRequirement>
  getTransferwiseAccountRequirements: (
    value: string,
    error: (errorMessage: string) => void
  ) => void
  specifyTransferwiseAccountRequirements: (
    currentCurrency: string,
    currentTransferType: string,
    details: { [key: string]: string | RecipientAddress },
    error: (errorMessage: string) => void
  ) => void
  bankDetailsForm: {
    address: {
      state?: string
    }
  }
  addTransferwiseRecipient: (
    currentCurrency: string | null,
    currentTransferType: string | null,
    bankDetailsForm: {
      address: {
        state?: string
      }
    },
    error: (error: { errorMessage: string }) => void
  ) => void
  countries: CountriesProps[]
  recipient: Recipient
}

const Payout: React.FunctionComponent<PayoutProps> = ({
  currencies,
  requirements,
  getTransferwiseAccountRequirements,
  specifyTransferwiseAccountRequirements,
  bankDetailsForm,
  addTransferwiseRecipient,
  countries,
  recipient,
}) => {
  const { t } = useTranslation()

  const [activeTab, setActiveTab] = useState('1')
  const [currentCurrency, setCurrentCurrency] = useState<string>()
  const [currentTransferType, setCurrentTransferType] = useState<string>()
  const [currentTypeRequirements, setCurrentTypeRequirements] = useState<
    TransferwiseRequirement
  >()
  const [isLoading, setIsLoading] = useState(false)
  const [isCurrencyLoading, setIsCurrencyLoading] = useState(false)
  const [isSubmitSuccessAlert, setIsSubmitSuccessAlert] = useState(false)
  const [isSubmitFailedAlert, setIsSubmitFailedAlert] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleChangeCurrency = ({ value }: { value: string }) => {
    setIsCurrencyLoading(true)
    setCurrentCurrency(value)
    getTransferwiseAccountRequirements(value, error => {
      if (!error) {
        setIsCurrencyLoading(false)
      }
    })
  }

  const toggle = (tab: string, type: string) => {
    setActiveTab(tab)
    setCurrentTransferType(type)
    setCurrentTypeRequirements(requirements.find(el => el.type === type))
  }

  useEffect(() => {
    if (requirements) {
      setCurrentTypeRequirements(requirements[+activeTab - 1])
    }
    if (recipient && !currentCurrency) {
      handleChangeCurrency({ value: recipient.currency })
    } else if (
      recipient &&
      !isEmpty(requirements) &&
      currentCurrency === recipient.currency
    ) {
      requirements.forEach(
        (el, index) =>
          !currentTransferType &&
          el.type === recipient.type &&
          toggle(`${index + 1}`, recipient.type)
      )
    } else if (!isEmpty(requirements)) {
      const selectedRequirements = requirements[+activeTab - 1]
      if (selectedRequirements) {
        toggle(activeTab, requirements[+activeTab - 1].type)
      } else {
        toggle('1', requirements[0].type)
      }
    }
  }, [recipient, requirements])

  const initialValues = useMemo(() => {
    // adding only necessary property's to form initial values
    const initialObj: { [key: string]: string | RecipientAddress } = {}

    if (recipient?.accountHolderName) {
      initialObj.accountHolderName = recipient.accountHolderName
    }

    if (recipient?.details?.address) {
      initialObj.address = recipient.details.address
    }

    if (recipient?.type && requirements && requirements.length > 0) {
      const currentRequirements = requirements.find(
        requirement => requirement.type === recipient.type
      )

      currentRequirements &&
        currentRequirements.fields.forEach(field => {
          field.group.forEach(groupItem => {
            // @ts-ignore
            if (recipient.details[groupItem.key]) {
              // @ts-ignore
              initialObj[groupItem.key] = recipient.details[groupItem.key]
            }
          })
        })
    }

    return initialObj
  }, [recipient, requirements])

  const setCurrenciesOptions = () =>
    currencies.map(currency => {
      const currencyCode = code(currency.code)

      return {
        value: currency.code,
        label: `${currency.code} - ${currencyCode && currencyCode.currency}`,
      }
    })

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const convertFormValues = form => {
    for (const key in form) {
      if (isObject(form[key]) && form[key].hasOwnProperty('value')) {
        form[key] = form[key].value
      } else if (isObject(form[key]) && !form[key].hasOwnProperty('value')) {
        convertFormValues(form[key])
      }
    }
  }

  const formDataMemo = useMemo(() => {
    const formDataNoState = { ...bankDetailsForm }
    if (formDataNoState?.address?.state) {
      delete formDataNoState.address.state
    }
    return formDataNoState
  }, [bankDetailsForm])

  const handleSubmit = () => {
    setIsCurrencyLoading(true)
    convertFormValues(bankDetailsForm)
    const isStatePresent = currentTypeRequirements?.fields.find(
      el => el.name === 'State'
    )
    addTransferwiseRecipient(
      currentCurrency || null,
      currentTransferType || null,
      isStatePresent ? bankDetailsForm : formDataMemo,
      error => {
        window.scrollTo(0, 0)
        if (error) {
          setErrorMessage(error.errorMessage || t('settingsPage.contact'))
          setIsSubmitFailedAlert(true)
          setIsSubmitSuccessAlert(false)
          setIsCurrencyLoading(false)
        } else {
          setIsSubmitFailedAlert(false)
          setIsSubmitSuccessAlert(true)
          setIsCurrencyLoading(false)
        }
      }
    )
  }

  const handleSpecifyRequirements = (val: { value: string }, key: string) => {
    setIsLoading(true)
    const details = {}
    if (key.includes('.')) {
      const arr = key.split('.')
      // @ts-ignore
      details[`${arr[0]}`] = {}
      // @ts-ignore
      details[`${arr[0]}`][`${arr[1]}`] = val.value
    } else {
      // @ts-ignore
      details[`${key}`] = val.value
    }
    specifyTransferwiseAccountRequirements(
      // @ts-ignore
      currentCurrency,
      currentTransferType,
      details,
      error => {
        if (!error) {
          setIsLoading(false)
        }
      }
    )
  }

  return (
    <>
      {isSubmitSuccessAlert && (
        <Alert
          color="success"
          icon
          onClose={() => setIsSubmitSuccessAlert(false)}
        >
          <div className="alert__text">
            <div className="alert__title">
              {t('settingsPage.transferwiseIsSetUp')}
            </div>
            <div>{t('settingsPage.canRequestPayouts')}</div>
          </div>
        </Alert>
      )}
      {isSubmitFailedAlert && (
        <Alert
          color="danger"
          icon
          onClose={() => setIsSubmitFailedAlert(false)}
        >
          <div className="alert__title">{t('errorsPage.error')}</div>
          <div>{errorMessage}</div>
        </Alert>
      )}
      <form className="form change_password_form currency_select_form">
        <p>{t('settingsPage.selectCurrencyDesc')}</p>
        <div className="select-currency-field-wrapp">
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('settingsPage.currency')}
            </span>
            <div className="form__form-group-field">
              <div className="form__form-group-field">
                <Field
                  component={renderSelectField}
                  defaultValue={recipient && recipient.currency}
                  menuPlacement="auto"
                  name="currency"
                  options={setCurrenciesOptions()}
                  placeholder={t('settingsPage.selectCurrency')}
                  required
                  onChange={handleChangeCurrency}
                />
              </div>
            </div>
          </div>
          <img alt="powered-by-transferwise" src={TransferwiseSvg} />
        </div>
        {!isEmpty(requirements) && <p>{t('settingsPage.inOrderToComply')}</p>}
      </form>
      {isCurrencyLoading ? (
        <ReactLoading
          className="users-loading loading"
          height={24}
          type="spinningBubbles"
        />
      ) : (
        <>
          {!isEmpty(requirements) && currentCurrency && (
            <Card className="tabs-wrapper">
              <div className="tabs tabs--bordered-bottom">
                <div className="tabs__wrap admin-tabs">
                  <Nav tabs>
                    {requirements
                      .filter(({ type }) => type !== 'email')
                      .map((el, index) => (
                        <NavItem key={el.title}>
                          <NavLink
                            className={classnames({
                              active: activeTab === `${index + 1}`,
                            })}
                            onClick={() => {
                              toggle(`${index + 1}`, el.type)
                            }}
                          >
                            {el.title} &ensp;
                          </NavLink>
                        </NavItem>
                      ))}
                  </Nav>
                </div>
              </div>
              <TabContent activeTab={activeTab}>
                {currentTypeRequirements && !isEmpty(currentTypeRequirements) && (
                  <>
                    {currentTypeRequirements.usageInfo && (
                      <p className="usage-info">
                        {currentTypeRequirements.usageInfo}
                      </p>
                    )}
                    <BankDetailsForm
                      item={currentTypeRequirements}
                      handleSpecifyRequirements={handleSpecifyRequirements}
                      isLoading={isLoading}
                      countries={countries}
                      recipient={recipient}
                      initialValues={
                        recipient &&
                        recipient.currency === currentCurrency &&
                        recipient.type === currentTransferType
                          ? initialValues
                          : {}
                      }
                      currentCurrency={currentCurrency}
                      currentTransferType={currentTransferType}
                      onSubmit={handleSubmit}
                    />
                  </>
                )}
              </TabContent>
            </Card>
          )}
        </>
      )}
    </>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default compose<any>(
  reduxForm({
    form: 'currency_form',
    validate,
  })
)(Payout)
